import { detectFieldIssue, FieldIssue } from './detectFieldIssue';

export interface FlaggedField {
  section: string;
  field: string;
  value: string;
  issue: FieldIssue;
}

/**
 * Checks whether any Thiqah- or Lean-provided field across the entire application
 * payload has a data quality issue (missing or Arabic value).
 *
 * This drives two behaviours in the Screening Result step:
 *  1. Auto-marks the "MISSING_WRONG_11111" high-risk reason as 'error' so reviewers
 *     are immediately alerted without having to open each section manually.
 *  2. Shows a red count badge on the "Missing/Wrong Retrieved Data" tab.
 */
export const checkApplicationNeedsFlag = (payload: any): boolean => {
  return getFlaggedFields(payload).length > 0;
};

/**
 * Collects every field (across Company Info, Bank Account, Managers, and
 * Individual Shareholders) that has a missing or Arabic value returned by
 * Thiqah / Lean.  The list is rendered in the dedicated review tab so agents
 * can see at a glance which fields still need manual correction.
 */
export const getFlaggedFields = (payload: any): FlaggedField[] => {
  const flaggedFields: FlaggedField[] = [];

  const companyInfo = payload?.companyInformation;
  const bankAccount = payload?.bankAccount;
  const managers: any[] = payload?.managers?.managers ?? [];
  const shareholders: any[] = payload?.shareholders?.individuals ?? [];

  const addIfIssue = (section: string, field: string, value?: string | null) => {
    const issue = detectFieldIssue(value);
    if (issue) {
      flaggedFields.push({ section, field, value: value ?? '', issue });
    }
  };

  // Legal/commercial names are sourced from Thiqah and can come back in Arabic or blank
  addIfIssue('Company Information', 'Legal Name', companyInfo?.legalName);
  addIfIssue('Company Information', 'Commercial Name', companyInfo?.commercialName);

  // Bank Account — Lean returns beneficiary and bank name; skip when user chose the
  // manual-upload path because those fields won't exist in that flow
  if (!bankAccount?.bankIntegrationRes?.showUploadField) {
    addIfIssue('Bank Account', 'Beneficiary Name', bankAccount?.accountHolderName);
    addIfIssue('Bank Account', 'Bank Name', bankAccount?.bankName);
  }

  // Manager identity fields come from Thiqah; all three are needed in English for
  // downstream processing (compliance checks, document generation, etc.)
  managers.forEach((m: any, i: number) => {
    const label = `Manager ${i + 1}`;
    addIfIssue(label, 'Full Name', m?.personalData?.fullName);
    addIfIssue(label, 'Nationality', m?.personalData?.nationality);
    addIfIssue(label, 'ID Number', m?.personalDocument?.idNumber);
  });

  // Same checks for individual shareholders — Thiqah data can be in Arabic or absent
  shareholders.forEach((s: any, i: number) => {
    const label = `Shareholder ${i + 1}`;
    addIfIssue(label, 'Full Name', s?.personalData?.fullName);
    addIfIssue(label, 'Nationality', s?.personalData?.nationality);
    addIfIssue(label, 'ID Number', s?.personalDocument?.idNumber);
  });

  return flaggedFields;
};
